import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import html2pdf from 'html2pdf.js';
import { useAuth } from '../contexts/AuthContext.tsx';
import JobInvoice from './JobInvoice.tsx';
import { INVOICE_CONSTANTS } from '../types/invoice.ts';

const ExcelInvoiceGenerator: React.FC = () => {
  const { currentUser } = useAuth();
  const [invoices, setInvoices] = useState<any>(null);
  const invoiceRefs = useRef<any>({});
  const summaryRef = useRef<HTMLDivElement>(null);
  const [savedInvoices, setSavedInvoices] = useState<any>({});
  const [notification, setNotification] = useState<any>(null);
  const [skippedPayTypes, setSkippedPayTypes] = useState<{ value: string; count: number }[]>([]);
  const [reconciliation, setReconciliation] = useState<{ sourceTotal: number; hasSourceAmounts: boolean } | null>(null);
  // Latest edited-but-not-yet-saved data per invoice (e.g. an expense added
  // in Edit Invoice mode), keyed by invoice number. Kept separate from
  // `invoices` (rather than overwriting it) so JobInvoice's `jobData` prop
  // never changes as a side effect of its own edits - that would re-fire its
  // data-init effect and re-fetch contractors from Firestore on every edit.
  const [liveInvoiceData, setLiveInvoiceData] = useState<{ [invoiceNumber: string]: any }>({});

  // Function to get client info based on invoice prefix
  const getClientInfo = useCallback((invoiceNumber: string) => {
    // Determine client based on invoice number prefix
    if (invoiceNumber.includes('BTC-')) {
      return INVOICE_CONSTANTS.CLIENTS.BTC;
    } else if (invoiceNumber.includes('REL-')) {
      return INVOICE_CONSTANTS.CLIENTS.RELIABLE;
    } else {
      return INVOICE_CONSTANTS.CLIENTS.CEC;
    }
  }, []);
  
  // Check for processed timesheet data in localStorage on component mount
  useEffect(() => {
    const processedData = localStorage.getItem('processedTimesheetData');
    if (processedData) {
      try {
        const parsedData = JSON.parse(processedData);
        
        // Handle both formats: 
        // 1. Flat array from BatchTimesheetToInvoiceConverter
        // 2. Object with timesheetData array from TimesheetProcessor
        let dataToProcess;
        if (Array.isArray(parsedData)) {
          // Format from BatchTimesheetToInvoiceConverter
          dataToProcess = parsedData;
        } else if (parsedData && parsedData.timesheetData && Array.isArray(parsedData.timesheetData)) {
          // Format from TimesheetProcessor
          dataToProcess = parsedData.timesheetData;
        }
        
        if (dataToProcess && dataToProcess.length > 0) {
          processTimesheetData(dataToProcess);
        }
      } catch (error) {
        console.error('Error parsing processed timesheet data:', error);
      }
    }
  }, []);

  // Memoized so its object identity is stable across re-renders - otherwise
  // every JobInvoice gets a "new" businessInfo prop on every render (e.g.
  // whenever any other invoice in the batch is saved), which defeats
  // React.memo and forces all invoices to re-render together.
  const businessInfo = useMemo(() => ({
    name: "7Twenty4 Services LLC",
    address: "200 Greenlea St",
    city: "Pulaski",
    state: "VA",
    zip: "24301",
    phone: "(972) 333-0913",
    email: "kim@7twenty4services.com",
    paymentTerms: "Net 30",
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()
  }), []);

  // Per-invoice summary (invoice #, job name, total hours, total $),
  // computed the same way JobInvoice does (activity totals when present,
  // else direct regular/overtime hours * rate, plus expenses). Each line
  // is rounded to the cent before summing (matching what's actually
  // printed on the invoice/PDF line items) to avoid floating-point drift
  // accumulating across hundreds of rows - otherwise this can be off by a
  // few cents from raw unrounded summation even when every row was
  // correctly included.
  const roundCents = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
  const invoiceSummaries = useMemo(() => {
    if (!invoices) return [];
    return Object.entries(invoices).map(([invNumber, originalJobData]: [string, any]) => {
      // Prefer the live edited copy (unsaved changes like an added expense)
      // over the original processed data, so the summary reflects what's
      // actually on screen right now.
      const jobData = liveInvoiceData[invNumber] || originalJobData;
      let regularHours = 0;
      let overtimeHours = 0;
      let amount = 0;
      Object.values(jobData.employees || {}).forEach((employee: any) => {
        if (employee.activities && Object.keys(employee.activities).length > 0) {
          Object.values(employee.activities).forEach((activity: any) => {
            regularHours += activity.regularHours || 0;
            overtimeHours += activity.overtimeHours || 0;
            amount += roundCents((activity.regularTotal || 0) + (activity.overtimeTotal || 0));
          });
        } else if (employee.regularHours !== undefined || employee.overtimeHours !== undefined) {
          regularHours += employee.regularHours || 0;
          overtimeHours += employee.overtimeHours || 0;
          amount += roundCents((employee.regularHours || 0) * (employee.regularRate || 0));
          amount += roundCents((employee.overtimeHours || 0) * (employee.overtimeRate || 0));
        }
        (employee.expenses || []).forEach((expense: any) => {
          amount += roundCents(parseFloat(expense.amount || 0));
        });
      });
      return {
        invoiceNumber: invNumber,
        jobName: jobData.jobName || '',
        totalHours: roundCents(regularHours + overtimeHours),
        totalAmount: roundCents(amount)
      };
    });
  }, [invoices, liveInvoiceData]);

  const invoicedTotal = useMemo(
    () => roundCents(invoiceSummaries.reduce((sum, s) => sum + s.totalAmount, 0)),
    [invoiceSummaries]
  );

  // Download the summary table as its own PDF, same approach JobInvoice
  // uses for individual invoices (html2pdf against a ref'd DOM node).
  const handleDownloadSummary = useCallback(() => {
    const printContent = summaryRef.current;
    if (!printContent) {
      console.error('Summary print content not found');
      return;
    }

    const opt = {
      margin: 10,
      filename: `Invoice Summary ${new Date().toLocaleDateString('en-US').replace(/\//g, '-')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
    };

    html2pdf().from(printContent).set(opt).save();
  }, []);

  // Stable identity across renders (not defined inline per-invoice) so
  // passing it as a prop doesn't force every JobInvoice in the batch to
  // re-render just because one of them saved.
  const handleInvoiceSaveSuccess = useCallback((invoiceNumber: string) => {
    setSavedInvoices(prev => ({
      ...prev,
      [invoiceNumber]: true
    }));
    setNotification({
      type: 'success',
      message: `Invoice #${invoiceNumber} saved successfully!`
    });
    // Auto-dismiss notification after 5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  }, []);

  // Keeps the batch summary (and reconciliation check) in sync with
  // unsaved edits made inside an invoice - e.g. adding an expense - since
  // those only live in that JobInvoice's local state until it's saved.
  const handleInvoiceDataChange = useCallback((invoiceNumber: string, data: any) => {
    setLiveInvoiceData((prev: any) => ({
      ...prev,
      [invoiceNumber]: data
    }));
  }, []);

  // Process data from the TimesheetProcessor component
  const processTimesheetData = (data: any) => {
    try {
      console.log('Processing timesheet data:', data);
      const processedInvoices = processJsonData(data);
      console.log('Processed invoices:', processedInvoices);
      setInvoices(processedInvoices);
      // Clear the localStorage data to prevent reprocessing on refresh
      localStorage.removeItem('processedTimesheetData');
    } catch (error) {
      console.error('Error processing timesheet data:', error);
    }
  };


  // Process data from TimesheetProcessor (JSON format)
  const processJsonData = useCallback((data: any) => {
    const invoices: any = {};
    const skippedPayTypeCounts: { [value: string]: number } = {};
    let sourceAmountTotal = 0;
    let hasSourceAmounts = false;

    console.log('Processing data rows:', data);

    for (const row of data) {
      console.log('Processing row:', row);
      const invoiceNumber = row['INV #'];
      const employee = row['EMPLOYEE'];
      const payType = row['PAY TYPE'];

      // Sum the raw Amount column from the source sheet across EVERY row
      // (including ones that end up skipped below) so we can reconcile it
      // against what actually lands in the generated invoices.
      if (row['SOURCE AMOUNT'] !== undefined && row['SOURCE AMOUNT'] !== null && row['SOURCE AMOUNT'] !== '') {
        const rowAmount = typeof row['SOURCE AMOUNT'] === 'string' ?
          parseFloat(row['SOURCE AMOUNT'].replace(/[^\d.-]/g, '')) :
          Number(row['SOURCE AMOUNT']);
        if (!isNaN(rowAmount)) {
          sourceAmountTotal += rowAmount;
          hasSourceAmounts = true;
        }
      }
      // Only use activity code and description if at least one of them is provided
      const activityCode = row['Activity Code'] || '';
      const activityDescription = row['Activity Description'] || '';
      const hasActivityMapping = activityCode.trim() !== '' || activityDescription.trim() !== '';
      
      console.log('Invoice number:', invoiceNumber, 'Employee:', employee);
      
      if (!invoiceNumber) {
        console.error('Invoice number is missing from row:', row);
        continue;
      }
      
      if (!invoices[invoiceNumber]) {
        // Get the week ending date and ensure it's in a valid format
        let weekEnding = row['WEEK ENDING'];
        try {
          // Try to parse the date - if it's already a valid date string, this will work
          const weekEndingDate = new Date(weekEnding);
          if (!isNaN(weekEndingDate.getTime())) {
            // Valid date, format it as YYYY-MM-DD
            weekEnding = weekEndingDate.toISOString().split('T')[0];
          } else {
            // If it's not a valid date, try to parse it as MM/DD/YYYY
            const parts = weekEnding.split('/');
            if (parts.length === 3) {
              const month = parseInt(parts[0], 10);
              const day = parseInt(parts[1], 10);
              const year = parseInt(parts[2], 10);
              const parsedDate = new Date(year, month - 1, day);
              if (!isNaN(parsedDate.getTime())) {
                weekEnding = parsedDate.toISOString().split('T')[0];
              }
            }
          }
        } catch (error) {
          console.error('Error formatting week ending date:', error);
          // Keep the original value if parsing fails
        }
        
        const clientInfo = getClientInfo(invoiceNumber);
        invoices[invoiceNumber] = {
          jobName: row['JOB NAME'],
          jobNumber: row['JOB NUMBER'],
          weekEnding: weekEnding,
          employees: {},
          clientName: clientInfo.name,
          clientAddress: clientInfo.address,
          clientCity: clientInfo.city,
          clientState: clientInfo.state,
          clientZip: clientInfo.zip,
          contractorId: row['CONTRACTOR_ID'] || '',
          contractorName: row['CONTRACTOR_NAME'] || '',
          paymentTerms: row['PAYMENT_TERMS'] || 'Net 30'
        };
      }

      if (!invoices[invoiceNumber].employees[employee]) {
        invoices[invoiceNumber].employees[employee] = {
          activities: {},
          expenses: []
        };
      }

      // Clean and parse numeric values
      const hours = typeof row['HOURS'] === 'string' ? 
        parseFloat(row['HOURS'].replace(/[^\d.-]/g, '')) : 
        (typeof row['HOURS'] === 'number' ? row['HOURS'] : 0);
      
      const burdenedRate = typeof row['BURDENED RATE'] === 'string' ?
        parseFloat(row['BURDENED RATE'].replace(/[^\d.-]/g, '')) :
        (typeof row['BURDENED RATE'] === 'number' ? row['BURDENED RATE'] : 0);

      // Case-insensitive, trimmed so "regular", "REGULAR ", etc. still
      // match. Anything else (Holiday, Vacation, Bonus...) is tracked
      // instead of silently dropped from every total.
      const normalizedPayType = String(payType || '').trim().toLowerCase();
      const isRegular = normalizedPayType === 'regular';
      const isOvertime = normalizedPayType === 'overtime';
      if (!isRegular && !isOvertime) {
        const label = String(payType || '').trim() || '(blank)';
        skippedPayTypeCounts[label] = (skippedPayTypeCounts[label] || 0) + 1;
      }

      // Only create activity entries if there's an activity mapping
      if (hasActivityMapping) {
        const activityKey = `${activityCode} - ${activityDescription}`;

        if (!invoices[invoiceNumber].employees[employee].activities[activityKey]) {
          invoices[invoiceNumber].employees[employee].activities[activityKey] = {
            activityCode,
            activityDescription,
            regularHours: 0,
            overtimeHours: 0,
            regularRate: 0,
            overtimeRate: 0,
            regularTotal: 0,
            overtimeTotal: 0
          };
        }

        const activity = invoices[invoiceNumber].employees[employee].activities[activityKey];

        if (isRegular) {
          activity.regularHours += hours;
          activity.regularRate = burdenedRate;
          // Round to 2 decimal places to match Excel precision
          activity.regularTotal = activity.regularHours * activity.regularRate;
          if (activity.overtimeRate === 0) {
            activity.overtimeRate = Math.round(burdenedRate * 1.5 * 100) / 100;
            console.log(`Rounding: Setting OT rate: $${burdenedRate} × 1.5 = $${burdenedRate * 1.5} → $${activity.overtimeRate}`);
          }
        } else if (isOvertime) {
          activity.overtimeHours += hours;
          activity.overtimeRate = burdenedRate;
          // Round to 2 decimal places to match Excel precision
          activity.overtimeTotal = activity.overtimeHours * activity.overtimeRate;
          if (activity.regularRate === 0) {
            activity.regularRate = burdenedRate / 1.5;
          }
        }
      } else {
        // If no activity mapping, just add the hours and rates directly to the employee
        if (!invoices[invoiceNumber].employees[employee].regularHours) {
          invoices[invoiceNumber].employees[employee].regularHours = 0;
          invoices[invoiceNumber].employees[employee].overtimeHours = 0;
          invoices[invoiceNumber].employees[employee].regularRate = 0;
          invoices[invoiceNumber].employees[employee].overtimeRate = 0;
          invoices[invoiceNumber].employees[employee].regularTotal = 0;
          invoices[invoiceNumber].employees[employee].overtimeTotal = 0;
        }

        if (isRegular) {
          invoices[invoiceNumber].employees[employee].regularHours += hours;
          invoices[invoiceNumber].employees[employee].regularRate = burdenedRate;
          // Round to 2 decimal places to match Excel precision
          invoices[invoiceNumber].employees[employee].regularTotal = invoices[invoiceNumber].employees[employee].regularHours * burdenedRate;
          if (invoices[invoiceNumber].employees[employee].overtimeRate === 0) {
            invoices[invoiceNumber].employees[employee].overtimeRate = Math.round(burdenedRate * 1.5 * 100) / 100;
            console.log(`Rounding: Setting employee OT rate: $${burdenedRate} × 1.5 = $${burdenedRate * 1.5} → $${invoices[invoiceNumber].employees[employee].overtimeRate}`);
          }
        } else if (isOvertime) {
          invoices[invoiceNumber].employees[employee].overtimeHours += hours;
          invoices[invoiceNumber].employees[employee].overtimeRate = burdenedRate;
          // Round to 2 decimal places to match Excel precision
          invoices[invoiceNumber].employees[employee].overtimeTotal = invoices[invoiceNumber].employees[employee].overtimeHours * burdenedRate;
          if (invoices[invoiceNumber].employees[employee].regularRate === 0) {
            invoices[invoiceNumber].employees[employee].regularRate = burdenedRate / 1.5;
          }
        }
      }
    }

    setSkippedPayTypes(
      Object.entries(skippedPayTypeCounts).map(([value, count]) => ({ value, count }))
    );
    setReconciliation({ sourceTotal: Math.round((sourceAmountTotal + Number.EPSILON) * 100) / 100, hasSourceAmounts });

    return invoices;
  }, [getClientInfo]);

  // Process data from Excel file
  const processExcelData = useCallback((data: any) => {
    const headers = data[0];
    const invoices: any = {};

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const invoiceNumber = row[headers.indexOf('INV #')];
      const employee = row[headers.indexOf('EMPLOYEE')];
      const payType = row[headers.indexOf('PAY TYPE')];
      // Only use activity code and description if at least one of them is provided
      const activityCode = row[headers.indexOf('Activity Code')] || '';
      const activityDescription = row[headers.indexOf('Activity Description')] || '';
      const hasActivityMapping = activityCode.trim() !== '' || activityDescription.trim() !== '';
      
      if (!invoices[invoiceNumber]) {
        // Get the week ending date and ensure it's in a valid format
        let weekEnding = row[headers.indexOf('WEEK ENDING')];
        try {
          // Try to parse the date - if it's already a valid date string, this will work
          const weekEndingDate = new Date(weekEnding);
          if (!isNaN(weekEndingDate.getTime())) {
            // Valid date, format it as YYYY-MM-DD
            weekEnding = weekEndingDate.toISOString().split('T')[0];
          } else {
            // If it's not a valid date, try to parse it as MM/DD/YYYY
            const parts = weekEnding.split('/');
            if (parts.length === 3) {
              const month = parseInt(parts[0], 10);
              const day = parseInt(parts[1], 10);
              const year = parseInt(parts[2], 10);
              const parsedDate = new Date(year, month - 1, day);
              if (!isNaN(parsedDate.getTime())) {
                weekEnding = parsedDate.toISOString().split('T')[0];
              }
            }
          }
        } catch (error) {
          console.error('Error formatting week ending date:', error);
          // Keep the original value if parsing fails
        }
        
        const clientInfo = getClientInfo(invoiceNumber);
        invoices[invoiceNumber] = {
          jobName: row[headers.indexOf('JOB NAME')],
          jobNumber: row[headers.indexOf('JOB NUMBER')],
          weekEnding: weekEnding,
          employees: {},
          clientName: clientInfo.name,
          clientAddress: clientInfo.address,
          clientCity: clientInfo.city,
          clientState: clientInfo.state,
          clientZip: clientInfo.zip
        };
      }

      if (!invoices[invoiceNumber].employees[employee]) {
        invoices[invoiceNumber].employees[employee] = {
          activities: {},
          expenses: []
        };
      }

      const hours = parseFloat(row[headers.indexOf('HOURS')]) || 0;
      const burdenedRate = parseFloat(row[headers.indexOf('BURDENED RATE')]) || 0;

      // Only create activity entries if there's an activity mapping
      if (hasActivityMapping) {
        const activityKey = `${activityCode} - ${activityDescription}`;

        if (!invoices[invoiceNumber].employees[employee].activities[activityKey]) {
          invoices[invoiceNumber].employees[employee].activities[activityKey] = {
            activityCode,
            activityDescription,
            regularHours: 0,
            overtimeHours: 0,
            regularRate: 0,
            overtimeRate: 0,
            regularTotal: 0,
            overtimeTotal: 0
          };
        }

        const activity = invoices[invoiceNumber].employees[employee].activities[activityKey];

        if (payType && payType.toLowerCase() === 'regular') {
          activity.regularHours += hours;
          activity.regularRate = burdenedRate;
          // Round to 2 decimal places to match Excel precision
          activity.regularTotal = activity.regularHours * activity.regularRate;
          if (activity.overtimeRate === 0) {
            activity.overtimeRate = Math.round(burdenedRate * 1.5 * 100) / 100;
            console.log(`Rounding: Setting OT rate: $${burdenedRate} × 1.5 = $${burdenedRate * 1.5} → $${activity.overtimeRate}`);
          }
        } else if (payType && payType.toLowerCase() === 'overtime') {
          activity.overtimeHours += hours;
          activity.overtimeRate = burdenedRate;
          // Round to 2 decimal places to match Excel precision
          activity.overtimeTotal = activity.overtimeHours * activity.overtimeRate;
          if (activity.regularRate === 0) {
            activity.regularRate = burdenedRate / 1.5;
          }
        }
      } else {
        // If no activity mapping, just add the hours and rates directly to the employee
        if (!invoices[invoiceNumber].employees[employee].regularHours) {
          invoices[invoiceNumber].employees[employee].regularHours = 0;
          invoices[invoiceNumber].employees[employee].overtimeHours = 0;
          invoices[invoiceNumber].employees[employee].regularRate = 0;
          invoices[invoiceNumber].employees[employee].overtimeRate = 0;
          invoices[invoiceNumber].employees[employee].regularTotal = 0;
          invoices[invoiceNumber].employees[employee].overtimeTotal = 0;
        }

        if (payType && payType.toLowerCase() === 'regular') {
          invoices[invoiceNumber].employees[employee].regularHours += hours;
          invoices[invoiceNumber].employees[employee].regularRate = burdenedRate;
          // Round to 2 decimal places to match Excel precision
          invoices[invoiceNumber].employees[employee].regularTotal = invoices[invoiceNumber].employees[employee].regularHours * burdenedRate;
          if (invoices[invoiceNumber].employees[employee].overtimeRate === 0) {
            invoices[invoiceNumber].employees[employee].overtimeRate = Math.round(burdenedRate * 1.5 * 100) / 100;
            console.log(`Rounding: Setting employee OT rate: $${burdenedRate} × 1.5 = $${burdenedRate * 1.5} → $${invoices[invoiceNumber].employees[employee].overtimeRate}`);
          }
        } else if (payType && payType.toLowerCase() === 'overtime') {
          invoices[invoiceNumber].employees[employee].overtimeHours += hours;
          invoices[invoiceNumber].employees[employee].overtimeRate = burdenedRate;
          // Round to 2 decimal places to match Excel precision
          invoices[invoiceNumber].employees[employee].overtimeTotal = invoices[invoiceNumber].employees[employee].overtimeHours * burdenedRate;
          if (invoices[invoiceNumber].employees[employee].regularRate === 0) {
            invoices[invoiceNumber].employees[employee].regularRate = burdenedRate / 1.5;
          }
        }
      }
    }

    return invoices;
  }, [getClientInfo]);



  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-8 bg-white shadow-xl rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
          <h1 className="text-3xl font-bold text-center mb-2">Invoice Generator</h1>
          <p className="text-center text-blue-100">Generate professional invoices from processed timesheet data</p>
        </div>
      </div>

      {!invoices && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <div className="max-w-md mx-auto">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Invoice Data</h3>
            <p className="text-gray-600 mb-6">
              No processed timesheet data found. To generate invoices, first convert a timesheet from the Timesheet Management page.
            </p>
            <div className="space-y-3">
              <a
                href="/timesheets"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Go to Timesheets
              </a>
              <p className="text-sm text-gray-500">
                From there, you can convert completed timesheets to invoices
              </p>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <div className={`mb-6 p-4 rounded-lg ${notification.type === 'success' ? 'bg-green-100 border-l-4 border-green-500 text-green-700' : 'bg-red-100 border-l-4 border-red-500 text-red-700'}`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setNotification(null)}
                  className={`inline-flex rounded-md p-1.5 ${notification.type === 'success' ? 'text-green-500 hover:bg-green-200' : 'text-red-500 hover:bg-red-200'} focus:outline-none`}
                >
                  <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {reconciliation && reconciliation.hasSourceAmounts && invoices && (() => {
        const diff = reconciliation.sourceTotal - invoicedTotal;
        const absDiff = Math.abs(diff);
        // A few cents of drift is expected: the source sheet rounds each
        // day-line to the cent, we round once per aggregated activity line,
        // and those don't always land the same. That's not lost data - a
        // real bug (a dropped row, job, or pay type) shows up as dollars,
        // not cents. $1 comfortably absorbs normal rounding noise on a
        // batch this size while still catching anything material.
        const TOLERANCE = 1.00;
        const matches = absDiff < TOLERANCE;
        const exact = absDiff < 0.01;
        return (
          <div className={`mb-6 rounded-lg p-4 border-l-4 print:hidden ${matches ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {matches ? (
                  <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <h3 className={`text-lg font-medium ${matches ? 'text-green-800' : 'text-red-800'}`}>
                  {matches ? 'Reconciliation check passed' : 'Reconciliation check FAILED — totals do not match'}
                </h3>
                <p className={`text-sm mt-1 ${matches ? 'text-green-700' : 'text-red-700'}`}>
                  Source Amount column total: <span className="font-semibold">${reconciliation.sourceTotal.toFixed(2)}</span>
                  {' '}&mdash; Total across all invoices below: <span className="font-semibold">${invoicedTotal.toFixed(2)}</span>
                  {!exact && (
                    <>
                      {' '}&mdash; Difference: <span className="font-semibold">${absDiff.toFixed(2)}</span> {diff > 0 ? 'missing from invoices' : 'extra in invoices'}
                      {matches && ' (normal rounding, not a concern)'}
                    </>
                  )}
                </p>
                {!matches && (
                  <p className="text-sm text-red-700 mt-1">
                    Do not send these invoices until this is resolved. Check the pay-type warning below if present, and verify no jobs/employees are missing from the invoices.
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {skippedPayTypes.length > 0 && (
        <div className="mb-6 bg-red-50 rounded-lg p-4 border-l-4 border-red-500 print:hidden">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800">
                {skippedPayTypes.reduce((sum, s) => sum + s.count, 0)} row(s) were NOT included in any invoice below
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Only rows whose pay type is exactly "Regular" or "Overtime" get totaled into an invoice. These pay types didn't match and their hours/amounts are missing from every total below:
              </p>
              <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                {skippedPayTypes.map(({ value, count }) => (
                  <li key={value}><span className="font-mono font-semibold">"{value}"</span> — {count} row(s)</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {invoices && invoiceSummaries.length > 0 && (
        <div className="bg-white shadow-lg rounded-lg p-6 print:shadow-none mt-8">
          <div className="flex justify-end mb-4 print:hidden">
            <button
              onClick={handleDownloadSummary}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Summary PDF
            </button>
          </div>
          <div ref={summaryRef}>
            <h2 className="text-2xl font-bold text-center mb-1">7Twenty4 Services LLC</h2>
            <p className="text-center text-gray-500 mb-6">Invoice Batch Summary &mdash; {invoiceSummaries.length} invoice{invoiceSummaries.length !== 1 ? 's' : ''}</p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 print-table">
                <thead>
                  <tr className="border-b-2 border-gray-800">
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Invoice #</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Job Name</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Total Hours</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceSummaries.map((s, i) => (
                    <tr key={s.invoiceNumber} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{s.invoiceNumber}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{s.jobName}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{s.totalHours.toFixed(2)}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">${s.totalAmount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-800 font-bold">
                    <td className="px-4 py-2 text-sm text-gray-900" colSpan={2}>Total</td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">
                      {roundCents(invoiceSummaries.reduce((sum, s) => sum + s.totalHours, 0)).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">${invoicedTotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {invoices && (
        <div className="space-y-8">

          {Object.entries(invoices).map(([invNumber, jobData]) => (
            <div key={invNumber} className="bg-white shadow-lg rounded-lg p-6 print:shadow-none mt-8 print:mt-0 page-break">
              <div ref={el => { if (el) invoiceRefs.current[invNumber] = el; }}>
                <JobInvoice 
                  jobData={jobData} 
                  invoiceNumber={invNumber}
                  useBtcPrefix={invNumber.includes('BTC-')}
                  useRelPrefix={invNumber.includes('REL-')}
                  businessInfo={businessInfo}
                  onSaveSuccess={handleInvoiceSaveSuccess}
                  onDataChange={handleInvoiceDataChange}
                />
              </div>
              {savedInvoices[invNumber] && (
                <div className="mt-4 p-3 bg-green-50 border-l-4 border-green-500 text-green-700 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">This invoice has been saved to the database</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExcelInvoiceGenerator;
